import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { Palette } from '../_theme/palette.js';
import { Spinner } from './Spinner.js';

interface SubmitButtonProps {
  palette: Palette;
  onClick: () => void;
  submitting: boolean;
  disabled?: boolean;
  // Optional trailing icon shown when idle (e.g. forward chevron). Hidden
  // automatically while submitting so the spinner replaces it cleanly.
  trailingIcon?: ReactNode;
  children: ReactNode;
}

// Width-locked submit button: when `submitting` flips true the button measures
// its current width and pins it, then swaps the label for an inline spinner.
// This avoids the layout jump from "Submit" (~70 px) to "Sending…" (~80 px)
// to a spinner, which would shift the surrounding footer mid-press.
export function SubmitButton({
  palette,
  onClick,
  submitting,
  disabled,
  trailingIcon,
  children,
}: SubmitButtonProps): JSX.Element {
  const ref = useRef<HTMLButtonElement>(null);
  const [pinnedWidth, setPinnedWidth] = useState<number | null>(null);

  useEffect(() => {
    if (submitting && ref.current && pinnedWidth === null) {
      // Measure synchronously so the next paint already has the locked width.
      setPinnedWidth(ref.current.getBoundingClientRect().width);
    } else if (!submitting && pinnedWidth !== null) {
      setPinnedWidth(null);
    }
  }, [submitting, pinnedWidth]);

  const merged: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 20px',
    borderRadius: palette.radius,
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    background: palette.primary,
    color: palette.onPrimary,
    cursor: disabled ? 'not-allowed' : submitting ? 'progress' : 'pointer',
    opacity: disabled && !submitting ? 0.6 : 1,
    width: pinnedWidth ?? undefined,
    minHeight: 40,
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled || submitting}
      className="chirp-btn"
      style={merged}
      aria-busy={submitting}
    >
      {submitting ? (
        <Spinner size={16} color={palette.onPrimary} palette={palette} />
      ) : (
        <>
          {children}
          {trailingIcon}
        </>
      )}
    </button>
  );
}
